<template>
  <div class="cell-container">
    <slot name="cell" v-bind="cellAttrs()" />
    <!-- v-if is required, otherwise they might not be cleanly removed fom the DOM, breaking tests -->
    <v-menu
      v-if="isEditing"
      v-model="isEditing"
      :nudge-left="width / 2"
      :close-on-content-click="false"
      :position-x="x"
      :position-y="y"
      absolute
      top
      offset-y
    >
      <bm-card data-qa="grid_cell_edit_dialog">
        <bm-form ref="form" @submit="submit">
          <v-container fluid pb-2>
            <div>{{ editRendererMetadata.editDialogTitle }}</div>
            <slot name="control" v-bind="controlAttrs()"> </slot>
          </v-container>
        </bm-form>

        <template #controls>
          <v-col cols="6" class="text-left pa-0">
            <bm-btn
              data-qa="cancel"
              icon
              x-large
              button-type="ghost"
              @click="cancel"
            >
              <v-icon> {{ cancelIcon }}</v-icon>
            </bm-btn>
          </v-col>
          <v-col cols="6" class="text-right pa-0">
            <bm-btn
              data-qa="accept"
              icon
              x-large
              button-type="ghost"
              @click="submit"
            >
              <v-icon> {{ acceptIcon }}</v-icon>
            </bm-btn>
          </v-col>
        </template>
      </bm-card>
    </v-menu>
  </div>
</template>

<script lang="ts">
import BmCard from "@/components/controls/card/BmCard.vue";
import type { EditableRendererMetadata } from "@/components/controls/grid/renderers/editors/types";
import type { Renderable } from "@/components/controls/grid/BmGridData";
import {
  prefix,
  suffix,
} from "@/components/controls/grid/renderers/rendererUtilities";
import type { PropType } from "vue";
import Vue, { computed } from "vue";
import { useThemedIcons, useVuetify } from "@/plugins/vuetify";

export default Vue.extend({
  name: "DataCellEditDialog",
  components: { BmCard },
  props: {
    item: {
      default: () => undefined,
    },
    value: {
      required: false,
    },
    context: {
      type: Object as PropType<Renderable>,
      required: false,
    },
  },
  setup() {
    const { icons } = useThemedIcons();
    const { $vuetify } = useVuetify();

    // only doing this to prevent type errors in the template
    // (won't need to do this after converting to composition API)
    const acceptIcon = computed(
      () => $vuetify.icons.values[icons.value.accept]
    );
    const cancelIcon = computed(
      () => $vuetify.icons.values[icons.value.cancel]
    );

    return {
      acceptIcon,
      cancelIcon,
    };
  },
  data() {
    return {
      isEditing: false as boolean,
      internalValue: null as any,
      x: 0 as number,
      y: 0 as number,
    };
  },
  computed: {
    width(): number {
      return this.editRendererMetadata.dialogWidth || 200;
    },
    editRendererMetadata(): EditableRendererMetadata {
      return this.context.rendererMetadata.editRenderer;
    },
  },
  watch: {
    isEditing: {
      // @ts-ignore
      handler: "isEditingWatch",
    },
    item: {
      // @ts-ignore
      handler: "itemWatch",
      deep: true,
    },
  },
  methods: {
    cellAttrs(): any {
      if (this.editRendererMetadata.rowEditable(this.item)) {
        return {
          props: this.$props,
          on: {
            edit: (event: MouseEvent) => {
              this.x = event.clientX;
              this.y = event.clientY - 5;

              this.isEditing = true;
            },
          },
        };
      } else {
        return {
          props: this.$props,
        };
      }
    },
    controlAttrs(): any {
      const controlAttrs = {
        props: {
          value: this.internalValue,
          prefix: prefix(this.context, this.item, this.internalValue),
          suffix: suffix(this.context, this.item, this.internalValue),
        },
        on: {
          input: (val: any) => (this.internalValue = val),
        },
      };

      Object.assign(
        controlAttrs.props,
        this.editRendererMetadata.editControlProps instanceof Function
          ? this.editRendererMetadata.editControlProps(this.item)
          : this.editRendererMetadata.editControlProps
      );

      return controlAttrs;
    },
    isEditingWatch(isEditing: boolean) {
      if (isEditing) {
        this.itemWatch(this.item);
        this.focusOnFormWhenReady();
      }
    },
    focusOnFormWhenReady(depth: number = 0): void {
      // Have to wait for the form element to be created, which may take a few ticks depending on depth
      if (depth > 10) {
        // prevent infinite recursion
        return;
      }
      if (this.$refs["form"] == undefined) {
        this.$nextTick(() => this.focusOnFormWhenReady(depth++));
      } else {
        (this.$refs["form"] as any).focus();
      }
    },
    itemWatch(newItem: any) {
      this.internalValue = this.editRendererMetadata.editItemValue(newItem);
    },
    validate(): boolean {
      return (this.$refs["form"] as any).validate();
    },
    cancel() {
      this.isEditing = false;
    },
    submit(event: Event) {
      // Prevent the page from refreshing as some users will press enter
      event.preventDefault();

      if (this.validate()) {
        this.editRendererMetadata.editInserter(this.item, this.internalValue);
        this.isEditing = false;
      }
    },
  },
});
</script>

<style scoped lang="sass">
@import "src/sass/typography"
@import "src/sass/colours"

// Collapse all the padding in the card
:deep(.v-card__text)
  padding: 0 !important

:deep(.v-card__actions)
  padding: 0 !important

.cell-container
  width: 100%
</style>
